import React, { useCallback, useMemo } from "react";
import styles from "./Button.module.css";
import { getClassName } from "../../utils/className.js";

import Spinner from "../../Icons/Spinner.jsx";

/**
 * @typedef {Object} ButtonProps
 * @property {import('react').ReactNode} [children="Button"] - The main button text, label, or children elements.
 * @property {'contained' | 'outlined' | 'text'} [variant="contained"] - Visual theme representation of the button.
 * @property {boolean | string} [white=false] - Applies a premium white/secondary theme, perfect for secondary cancel buttons.
 * @property {boolean} [red=false] - Applies a bold red/destructive theme, ideal for delete or discard actions.
 * @property {number | string} [width] - Sets a precise fixed width for the button (in pixels).
 * @property {boolean} [marginRight=false] - Appends a standard 20px margin to the right side of the button.
 * @property {boolean} [uppercase=false] - Transforms the button label/children to uppercase formatting.
 * @property {boolean} [bold=false] - Toggles a bold font weight (implicitly active when using the secondary white theme).
 * @property {boolean} [disableRipple=false] - Selectively turns off the native material-like active touch ripple effect.
 * @property {boolean} [loading=false] - Shows a spinner inside the button and disables all interactions.
 * @property {boolean} [hideTextOnLoading=false] - Fades out the button text/icons and displays only the loader icon when loading is true.
 * @property {{ root?: string }} [classes] - Nested styles object to apply custom classes (e.g. `{ root }`).
 * @property {string} [color] - Allows passing a custom theme color to directly override background, text, or border properties.
 * @property {import('react').ReactNode} [startIcon] - Optional React element to render before the children.
 * @property {import('react').ReactNode} [endIcon] - Optional React element to render after the children.
 * @property {'small' | 'medium' | 'large'} [size] - Controls the height and font size of the button.
 */

/**
 * @param {import('react').ButtonHTMLAttributes<HTMLButtonElement> & ButtonProps} props
 */

const Button = ({
  children = "Button",
  startIcon,
  endIcon,
  onClick,
  onMouseUp,
  onMouseDown,
  autoFocus = false,
  variant = "contained",
  type = "button",
  title,
  disabled = false,
  white = false,
  red = false,
  width,
  marginRight = false,
  uppercase = false,
  bold = false,
  disableRipple = false,
  loading = false,
  hideTextOnLoading = false,
  style,
  classes,
  color,
  className: externalClassName,
  size,
  ...rest
}) => {

  const variantClass =
    variant === "outlined"
      ? styles.outlined
      : variant === "text"
        ? styles.text
        : null;

  const sizeClass = size === "small" ? styles.small : size === "medium" ? styles.medium : size === "large" ? styles.large : null;

  const externalHasBackground = useMemo(() => {
    if (!externalClassName || typeof document === "undefined") {
      return false;
    }
    // Only a selector that targets the class on its own (".x", "button.x",
    // ".x:hover") styles this button wherever it renders. A descendant or
    // sibling selector (".someDialog .x") is scoped to that context, so its
    // background must not make every button carrying the class drop its own.
    const standalone = externalClassName
      .trim()
      .split(/\s+/)
      .map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .map((c) => new RegExp(`^[^\\s>+~]*\\.${c}(?![\\w-])[^\\s>+~]*$`));
    const targetsClass = (selectorText) =>
      selectorText
        .split(",")
        .some((selector) => standalone.some((re) => re.test(selector.trim())));
    for (const sheet of document.styleSheets) {
      try {
        for (const rule of sheet.cssRules) {
          if (!rule.selectorText) {
            continue;
          }
          if (targetsClass(rule.selectorText)) {
            const { background, backgroundColor, backgroundImage } = rule.style;
            if (background || backgroundColor || backgroundImage) return true;
          }
        }
      } catch (_) { }
    }
    return false;
  }, [externalClassName]);

  const className = getClassName([
    styles.button,
    sizeClass,
    variantClass ||
    (white
      ? styles.white
      : red
        ? styles.red
        : !externalHasBackground
          ? styles.general
          : null),
    {
      [styles.uppercase]: uppercase,
      [styles.bold]: white || bold,
      [styles.loadingShrunk]: loading && hideTextOnLoading,
    },
    classes?.root,
    externalClassName,
  ]);

  const colorStyle = color
    ? variant === "outlined" || variant === "text"
      ? { color, borderColor: color, ...(disabled ? { opacity: 0.5 } : {}) }
      : { background: color, ...(disabled ? { opacity: 0.5 } : {}) }
    : {};

  const inlineStyle = {
    ...(width ? { minWidth: `${width}px`, width: `${width}px` } : {}),
    ...(marginRight ? { marginRight: "20px" } : {}),
    ...colorStyle,
    ...style,
  };

  const spawnRipple = useCallback((button, clientX, clientY) => {
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;
    const rect = button.getBoundingClientRect();
    const ripple = document.createElement("span");
    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${(clientX ?? rect.left + rect.width / 2) - rect.left - radius}px`;
    ripple.style.top = `${(clientY ?? rect.top + rect.height / 2) - rect.top - radius}px`;
    ripple.className = styles.ripple;
    button.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);
  }, []);

  const handleMouseDown = useCallback(
    (e) => {
      if (!disabled && !loading && !disableRipple) {
        spawnRipple(e.currentTarget, e.clientX, e.clientY);
      }
      onMouseDown?.(e);
    },
    [disabled, loading, disableRipple, spawnRipple, onMouseDown],
  );

  const handleClick = useCallback(
    (e) => {
      if (disabled || loading) {
        return;
      }
      onClick?.(e);
    },
    [disabled, loading, onClick],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (disabled || loading) {
        return;
      }
      if (!disableRipple && (e.key === "Enter" || e.key === " ")) {
        spawnRipple(e.currentTarget, null, null);
      }
    },
    [disabled, loading, spawnRipple],
  );

  const handleMouseUp = useCallback(
    (e) => {
      onMouseUp?.(e);
    },
    [onMouseUp],
  );

  return (
    <button
      className={className}
      style={inlineStyle}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      type={type}
      title={title}
      disabled={disabled || loading}
      autoFocus={autoFocus}
      {...rest}
    >
      {loading && (
        <span className={styles.spinnerWrapper}>
          <Spinner size={size === "small" ? 14 : size === "medium" ? 16 : 18} />
        </span>
      )}
      {startIcon && !loading && (
        <span className={styles.startIcon}>{startIcon}</span>
      )}
      {children}
      {endIcon && <span className={styles.endIcon}>{endIcon}</span>}
    </button>
  );
};

export default Button;
